import { useMemo, useState } from 'preact/hooks';
import { useFilter } from '../../contexts/FilterContext';
import type { ChartSeries } from '../../types/charts';
import {
  buildIncomeVsExpensesData,
  buildNetCashflowData,
  buildPortfolioValueData,
  type PeriodMode,
} from '../../functions/calculations';
import {
  isWithinPeriodWindow,
  parseChartMonthLabel,
  resolvePeriodWindow,
  statementDateKeyFromDate,
} from '../../functions/calculations/unitFinancials';
import type { OverviewChartType, OverviewViewBy } from './overview.config';
import { usePortfolioOverview } from './usePortfolioOverview';

export type ChartYearRange = {
  start: string;
  end: string;
};

function extractYearFromPoint(x: string | number | Date, viewBy: OverviewViewBy): string | null {
  const value = String(x);
  if (viewBy === 'year' && /^\d{4}$/.test(value)) {
    return value;
  }
  const yearMatch = value.match(/'(\d{2})$/);
  if (yearMatch) return `20${yearMatch[1]}`;
  if (/^\d{4}$/.test(value)) return value;
  return null;
}

function clampYearRange(
  range: ChartYearRange | null,
  yearsAsc: string[],
): ChartYearRange | null {
  if (yearsAsc.length === 0) return null;
  const minYear = yearsAsc[0];
  const maxYear = yearsAsc[yearsAsc.length - 1];
  if (!range) return { start: minYear, end: maxYear };

  const start = yearsAsc.includes(range.start) ? range.start : minYear;
  const end = yearsAsc.includes(range.end) ? range.end : maxYear;
  return start <= end ? { start, end } : { start: end, end: start };
}

function findLatestChartMonthKey(series: ChartSeries[]): string | null {
  let latestNonZero: Date | null = null;
  let latestAny: Date | null = null;

  series.forEach((item) => {
    item.data.forEach((point) => {
      const date = parseChartMonthLabel(String(point.x));
      if (!date) return;
      if (!latestAny || date > latestAny) latestAny = date;
      if (point.y !== 0 && (!latestNonZero || date > latestNonZero)) {
        latestNonZero = date;
      }
    });
  });

  const anchor = latestNonZero ?? latestAny;
  return anchor ? statementDateKeyFromDate(anchor) : null;
}

function clipSeriesToPeriod(series: ChartSeries[], periodMode: PeriodMode): ChartSeries[] {
  if (periodMode === 'all') return series;

  const periodWindow = resolvePeriodWindow(periodMode, findLatestChartMonthKey(series));

  return series.map((item) => ({
    ...item,
    data: item.data.filter((point) => {
      const date = parseChartMonthLabel(String(point.x));
      return date ? isWithinPeriodWindow(date, periodWindow) : false;
    }),
  }));
}

function aggregateSeriesByYear(series: ChartSeries[], selectedChart: OverviewChartType): ChartSeries[] {
  return series.map((item) => {
    const yearlyMap = new Map<string, number>();
    item.data.forEach((point) => {
      const year = extractYearFromPoint(point.x, 'month');
      if (!year) return;
      if (selectedChart === 'portfolio') {
        yearlyMap.set(year, point.y);
      } else {
        yearlyMap.set(year, (yearlyMap.get(year) || 0) + point.y);
      }
    });

    return {
      ...item,
      data: Array.from(yearlyMap.entries())
        .sort((left, right) => left[0].localeCompare(right[0]))
        .map(([year, value]) => ({ x: year, y: value })),
    };
  });
}

function filterSeriesByYear(series: ChartSeries[], range: ChartYearRange, viewBy: OverviewViewBy): ChartSeries[] {
  return series.map((item) => ({
    ...item,
    data: item.data.filter((point) => {
      const year = extractYearFromPoint(point.x, viewBy);
      if (!year) return false;
      return year >= range.start && year <= range.end;
    }),
  }));
}

function yearsInSeries(series: ChartSeries[]): string[] {
  const years = new Set<string>();
  series.forEach((item) => {
    item.data.forEach((point) => {
      const year = extractYearFromPoint(point.x, 'month');
      if (year) years.add(year);
    });
  });

  return Array.from(years).sort((left, right) => left.localeCompare(right));
}

export function useOverviewTrendChart(periodMode: PeriodMode) {
  const { selectedUnit, selectedUnitDetails, currencies } = useFilter();
  const unitCurrency = selectedUnitDetails?.currency || 'USD';

  const [selectedChart, setSelectedChart] = useState<OverviewChartType>('portfolio');
  const [viewBy, setViewBy] = useState<OverviewViewBy>('month');
  const [yearRange, setYearRange] = useState<ChartYearRange | null>(null);
  const overview = usePortfolioOverview();
  const rawSeries = useMemo(() => {
    if (!overview.data) return [];
    const selectedUnitId = selectedUnit?.unit_id_no;
    const onlySelectedUnit = <T extends { unit_id_no: number }>(rows: T[]) =>
      selectedUnitId ? rows.filter((row) => row.unit_id_no === selectedUnitId) : rows;
    if (selectedChart === 'portfolio') {
      return buildPortfolioValueData(
        onlySelectedUnit(overview.data.balance_sheet),
        overview.data.units,
        unitCurrency,
        currencies,
      );
    }
    if (selectedChart === 'incomeExpenses') {
      return buildIncomeVsExpensesData(
        onlySelectedUnit(overview.data.income_statement),
        overview.data.units,
        unitCurrency,
        currencies,
      );
    }
    return buildNetCashflowData(
      onlySelectedUnit(overview.data.cashflow),
      overview.data.units,
      unitCurrency,
      currencies,
    );
  }, [overview.data, selectedChart, selectedUnit?.unit_id_no, unitCurrency, currencies]);

  const availableYears = useMemo(() => yearsInSeries(rawSeries), [rawSeries]);
  const dateAnchorKey = useMemo(() => findLatestChartMonthKey(rawSeries), [rawSeries]);

  const series = useMemo(() => {
    const clipped = clipSeriesToPeriod(rawSeries, periodMode);
    const yearly = viewBy === 'year' ? aggregateSeriesByYear(clipped, selectedChart) : clipped;
    const filterRange = periodMode === 'all' && viewBy === 'month' ? yearRange : null;

    if (!filterRange) return yearly;
    return filterSeriesByYear(yearly, filterRange, viewBy);
  }, [rawSeries, periodMode, viewBy, selectedChart, yearRange]);

  const selectChart = (id: OverviewChartType) => {
    setSelectedChart(id);
    setYearRange(null);
  };

  const updateYearRange = (next: ChartYearRange) => {
    const clamped = clampYearRange(next, availableYears);
    if (!clamped) return;
    setYearRange(clamped);
  };

  return {
    unitCurrency,
    selectedChart,
    viewBy,
    yearRange,
    availableYears,
    dateAnchorKey,
    series,
    loading: overview.loading,
    periodMode,
    setViewBy,
    setYearRange: updateYearRange,
    selectChart,
  };
}
